import { format, isAfter, subMinutes } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeftRight, Download, FileSearch, Info, Search, Trash2 } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { FileCard } from "@/components/files/file-card";
import { FileDropzone } from "@/components/files/file-dropzone";
import { FilePreviewDialog, type PreviewFile } from "@/components/files/file-preview-dialog";
import { fileToPayload } from "@/components/files/use-file-to-payload";
import { usePreviewPrefetch } from "@/components/files/use-preview-prefetch";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCurrentUser } from "@/hooks/use-current-user";
import { config } from "@/lib/config";
import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../api/use-claim";
import {
  useAddClaimDocument,
  useAddProcedureDocument,
  useDeleteClaimDocument,
  useDeleteProcedureDocument,
  usePullClaimRealDocuments,
} from "../api/use-claim-mutations";
import {
  type ClaimDocumentType,
  filterDocumentGroups,
  getExtColor,
  getFileExtension,
  groupDocuments,
  type ProcedureDocumentType,
  procedureDocumentTypesMap,
  typeTranslations,
} from "../lib/document-types";
import { ChangeDocumentTypeDialog, type DocumentToRetype } from "./change-document-type-dialog";
import { ClaimDocumentFields } from "./claim-document-fields";

type ClaimDocumentItem = ClaimWithRelations["claimDocuments"][number];
type ProcedureDocumentItem = NonNullable<
  NonNullable<ClaimWithRelations["procedure"]>["procedureDocuments"]
>[number];

export function toPreviewFile(file: ClaimDocumentItem["file"]): PreviewFile {
  return {
    id: file.id,
    name: file.name ?? null,
    mimeType: file.mimeType ?? null,
    size: file.size ?? null,
    boxFileId: file.boxFileId ?? null,
  };
}

function addedAtLabel(createdAt: Date | string): string {
  return `Ajouté le ${format(new Date(createdAt), "d MMMM 'à' HH'h'mm", { locale: fr })}`;
}

/** Thumbnail (or colored extension placeholder) with a hover preview — mirrors the Vue thumbnail tooltip. */
function DocumentThumbnail({
  file,
  createdAt,
  compact,
}: {
  file: ClaimDocumentItem["file"];
  createdAt: Date | string;
  compact: boolean;
}) {
  const extColor = getExtColor(file.name ?? "");

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <div
            className={cn("flex-shrink-0 overflow-hidden rounded-lg", compact ? "h-7 w-7" : "h-10 w-10")}
            style={{
              background: file.thumbnailUrl ? "#f4f4f4" : extColor.bg,
              border: file.thumbnailUrl ? "1px solid #e5e7eb" : "none",
            }}
          />
        }
      >
        {file.thumbnailUrl ? (
          <img src={file.thumbnailUrl} alt={file.name ?? ""} className="h-full w-full object-cover" />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center font-bold uppercase"
            style={{ color: extColor.text, fontSize: compact ? "8px" : "11px" }}
          >
            {getFileExtension(file.name ?? "")}
          </div>
        )}
      </TooltipTrigger>
      <TooltipContent>
        {file.thumbnailUrl ? (
          <div className="max-w-[420px] p-1">
            <img
              src={file.thumbnailUrl}
              alt={file.name ?? ""}
              className="max-h-[400px] w-full rounded-md border border-gray-200 bg-gray-100 object-contain"
            />
            <p className="mt-2 truncate text-xs">{file.name}</p>
            <p className="mt-0.5 text-[11px] opacity-80">{addedAtLabel(createdAt)}</p>
          </div>
        ) : (
          addedAtLabel(createdAt)
        )}
      </TooltipContent>
    </Tooltip>
  );
}

interface AddClaimDocumentDialogProps {
  claim: ClaimWithRelations;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Port of the AlertModal « Ajouter un document » of ClaimDocuments.vue (Box picker excluded). */
function AddClaimDocumentDialog({ claim, open, onOpenChange }: AddClaimDocumentDialogProps) {
  const addDocument = useAddClaimDocument(claim.id);

  const [files, setFiles] = useState<File[]>([]);
  const [documentType, setDocumentType] = useState<ClaimDocumentType>("OTHER");
  const [selectedUserClaimId, setSelectedUserClaimId] = useState<string | null>(null);

  function reset() {
    setFiles([]);
    setDocumentType("OTHER");
    setSelectedUserClaimId(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      reset();
    }
    onOpenChange(nextOpen);
  }

  async function submit() {
    if (!files.length) {
      notify.warning("Aucun document sélectionné");
      return;
    }
    try {
      for (const file of files) {
        const payload = await fileToPayload(file);
        await addDocument.mutateAsync({
          name: payload.filename,
          document: payload.dataUrl,
          mimeType: payload.mimeType,
          claimDocumentType: documentType,
          userClaimId: selectedUserClaimId,
        });
      }
      handleOpenChange(false);
    } catch {
      // Error toasts handled by notify.promise.
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Ajouter un document</DialogTitle>
          <DialogDescription>Sélectionner un document à ajouter au dossier.</DialogDescription>
        </DialogHeader>

        <DialogPanel className="space-y-3">
          <FileDropzone multiple onFiles={(newFiles) => setFiles((current) => [...current, ...newFiles])} />
          {files.map((file, index) => (
            <FileCard
              key={`${file.name}-${index}`}
              name={file.name}
              size={file.size}
              mimeType={file.type}
              onRemove={() => setFiles((current) => current.filter((_, i) => i !== index))}
            />
          ))}

          {files.length ? (
            <ClaimDocumentFields
              passengers={claim.usersClaims}
              documentType={documentType}
              onDocumentTypeChange={setDocumentType}
              userClaimId={selectedUserClaimId}
              onUserClaimIdChange={setSelectedUserClaimId}
            />
          ) : null}
        </DialogPanel>

        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
          <Button onClick={submit} disabled={!files.length || addDocument.isPending}>
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Staging and the PR sandboxes only: the claim's real documents, watermarked SANDBOX, ID cards as SPÉCIMEN. */
function PullRealDocumentsButton({ claimId, size }: { claimId: string; size?: "sm" }) {
  const pullRealDocuments = usePullClaimRealDocuments(claimId);
  if (config.APP_ENV === "production") {
    return null;
  }

  return (
    <Button size={size} onClick={() => pullRealDocuments.mutate()} loading={pullRealDocuments.isPending}>
      <Download />
      Télécharger les pièces réelles
    </Button>
  );
}

interface ClaimDocumentsProps {
  claim: ClaimWithRelations;
  disabledDocumentIds?: string[];
}

/** Full port of overview/ClaimDocuments.vue — search, grouping by type, preview, add, delete. */
export function ClaimDocuments({ claim, disabledDocumentIds = [] }: ClaimDocumentsProps) {
  const { can } = useCurrentUser();
  const deleteDocument = useDeleteClaimDocument(claim.id);
  const prefetchPreview = usePreviewPrefetch();

  const [searchQuery, setSearchQuery] = useState("");
  const [previewedFile, setPreviewedFile] = useState<PreviewFile | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [documentToRetype, setDocumentToRetype] = useState<DocumentToRetype | null>(null);

  const files = claim.claimDocuments;
  const filteredGroups = filterDocumentGroups(groupDocuments(files), searchQuery);
  const canManage = can([...UPCLAIM_ROLES, "LAWYER"]);

  return (
    <div className="py-4">
      <FilePreviewDialog
        open={previewedFile !== null}
        file={previewedFile}
        onClose={() => setPreviewedFile(null)}
      />
      <AddClaimDocumentDialog claim={claim} open={addOpen} onOpenChange={setAddOpen} />
      <ChangeDocumentTypeDialog
        claimId={claim.id}
        document={documentToRetype}
        onClose={() => setDocumentToRetype(null)}
      />

      {/* Search bar */}
      {files.length ? (
        <div className="relative mb-2">
          <Search className="absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Rechercher un document..."
            className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pr-3 pl-9 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>
      ) : null}

      {filteredGroups.length ? (
        <div className="col-span-3 mb-5 w-full divide-y divide-gray-100 rounded-md border border-gray-300 bg-white shadow-sm">
          {filteredGroups.map((group) => (
            <div key={group.type}>
              {/* Group header (only shown when multiple docs of same type) */}
              {group.documents.length > 1 ? (
                <div className="border-b border-dotted border-zinc-400 bg-gray-50 py-2.5 pr-3 pl-2">
                  <span className="text-xs font-semibold tracking-wide text-gray-700 uppercase">
                    {group.label} (<span className="text-blue-700">{group.documents.length}</span>)
                  </span>
                </div>
              ) : null}

              <ul>
                {group.documents.map((doc) => {
                  const compact = group.documents.length > 1;
                  const isDisabled = disabledDocumentIds.includes(doc.id);
                  const isRecent = isAfter(new Date(doc.file.updatedAt), subMinutes(new Date(), 10));

                  return (
                    <li
                      key={doc.id}
                      {...prefetchPreview(toPreviewFile(doc.file))}
                      className={cn(
                        "group flex items-center justify-between text-sm leading-6",
                        compact ? "px-3 py-2" : "py-3 pr-5 pl-3",
                        isDisabled && "cursor-not-allowed opacity-40",
                      )}
                    >
                      <button
                        type="button"
                        className="flex w-0 flex-1 cursor-pointer items-center text-left"
                        onClick={() => setPreviewedFile(toPreviewFile(doc.file))}
                      >
                        <DocumentThumbnail file={doc.file} createdAt={doc.createdAt} compact={compact} />
                        <div className="ml-3 flex min-w-0 flex-1 items-center gap-2">
                          <span className={cn("truncate font-medium", compact && "text-xs text-gray-800")}>
                            {compact || doc.documentType.type === "OTHER"
                              ? doc.file.name
                              : typeTranslations[doc.documentType.type]}
                          </span>
                          {isRecent ? (
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
                              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-500" />
                            </span>
                          ) : null}
                        </div>
                      </button>
                      <div className="ml-4 flex flex-shrink-0 space-x-4">
                        {canManage ? (
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <button
                                  type="button"
                                  onClick={() =>
                                    setDocumentToRetype({
                                      fileId: doc.file.id,
                                      name: doc.file.name ?? typeTranslations[doc.documentType.type],
                                      documentType: doc.documentType.type,
                                    })
                                  }
                                  // Out of the way until the row is pointed at or the button reached from the
                                  // keyboard. Not on focus-within: the button keeps the focus after a click.
                                  className="rounded-md font-medium text-gray-500 opacity-0 group-hover:opacity-100 hover:text-gray-800 focus-visible:opacity-100 pointer-coarse:opacity-100"
                                  aria-label="Changer le type du document"
                                />
                              }
                            >
                              <ArrowLeftRight className="h-5 w-5" />
                            </TooltipTrigger>
                            <TooltipContent>Changer le type</TooltipContent>
                          </Tooltip>
                        ) : null}
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <button
                                type="button"
                                onClick={() => setPreviewedFile(toPreviewFile(doc.file))}
                                className="rounded-md font-medium text-blue-600 hover:text-blue-500"
                                aria-label="Prévisualiser le document"
                              />
                            }
                          >
                            <FileSearch className="h-5 w-5" />
                          </TooltipTrigger>
                          <TooltipContent>{addedAtLabel(doc.createdAt)}</TooltipContent>
                        </Tooltip>
                        {canManage ? (
                          <>
                            <span className="text-gray-200" aria-hidden="true">
                              |
                            </span>
                            <button
                              type="button"
                              className="rounded-md font-medium text-gray-900 hover:text-gray-800"
                              onClick={() => deleteDocument.mutate(doc.id)}
                              aria-label="Supprimer le document"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      ) : !files.length ? (
        <ul className="col-span-3 mb-5 w-full divide-y divide-gray-100 rounded-md border border-dotted border-gray-300 bg-white shadow-sm">
          <li className="flex items-center justify-between py-4 pr-5 pl-4 text-sm leading-6">
            <div className="flex w-0 flex-1 items-center">
              <Info className="ml-2 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              <div className="ml-4 flex min-w-0 flex-1 gap-2">
                <span className="flex items-center truncate font-medium">Aucun fichier pour l'instant</span>
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
                    Ajouter
                  </Button>
                  <PullRealDocumentsButton claimId={claim.id} size="sm" />
                </div>
              </div>
            </div>
          </li>
        </ul>
      ) : (
        <div className="col-span-3 mb-5 w-full rounded-md border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-center py-6 text-sm text-gray-400">
            Aucun document trouvé
          </div>
        </div>
      )}

      {files.length ? (
        <div className="flex gap-2 text-left">
          <Button variant="outline" onClick={() => setAddOpen(true)}>
            Ajouter un document
          </Button>
          <PullRealDocumentsButton claimId={claim.id} />
        </div>
      ) : null}
    </div>
  );
}

interface AddProcedureDocumentDialogProps {
  claim: ClaimWithRelations;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Port of the AlertModal « Ajouter un document officiel » of ProcedureDocuments.vue. */
function AddProcedureDocumentDialog({ claim, open, onOpenChange }: AddProcedureDocumentDialogProps) {
  const addDocument = useAddProcedureDocument(claim.id);

  const [files, setFiles] = useState<File[]>([]);
  const [documentType, setDocumentType] = useState<ProcedureDocumentType>("NOTICE");

  function reset() {
    setFiles([]);
    setDocumentType("NOTICE");
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      reset();
    }
    onOpenChange(nextOpen);
  }

  async function submit() {
    if (!files.length) {
      notify.warning("Aucun document sélectionné");
      return;
    }
    const procedureId = claim.procedure?.id;
    if (!procedureId) {
      notify.info("Impossible de détecter la procédure", {
        description: "Veuillez nous contacter si le problème persiste",
      });
      return;
    }
    try {
      for (const file of files) {
        const payload = await fileToPayload(file);
        await addDocument.mutateAsync({
          name: payload.filename,
          document: payload.dataUrl,
          mimeType: payload.mimeType,
          procedureDocumentType: documentType,
          procedureId,
          source: "MANUAL",
        });
      }
      handleOpenChange(false);
    } catch {
      // Error toasts handled by notify.promise.
    }
  }

  const typeOptions = Object.entries(procedureDocumentTypesMap) as Array<
    [ProcedureDocumentType, { label: string }]
  >;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Ajouter un document officiel</DialogTitle>
          <DialogDescription>Sélectionner un document à ajouter à la procédure.</DialogDescription>
        </DialogHeader>

        <DialogPanel className="space-y-3">
          <FileDropzone multiple onFiles={(newFiles) => setFiles((current) => [...current, ...newFiles])} />
          {files.map((file, index) => (
            <FileCard
              key={`${file.name}-${index}`}
              name={file.name}
              size={file.size}
              mimeType={file.type}
              onRemove={() => setFiles((current) => current.filter((_, i) => i !== index))}
            />
          ))}

          {files.length ? (
            <div className="py-2 text-left">
              <Label className="mb-1 block text-sm font-medium text-gray-700">Type de document</Label>
              <Select
                value={documentType}
                onValueChange={(value) => {
                  if (typeof value === "string") {
                    setDocumentType(value as ProcedureDocumentType);
                  }
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue>{procedureDocumentTypesMap[documentType].label}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map(([type, { label }]) => (
                    <SelectItem key={type} value={type}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </DialogPanel>

        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
          <Button onClick={submit} disabled={!files.length || addDocument.isPending}>
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Full port of overview/ProcedureDocuments.vue — list, preview, add, delete. */
export function ProcedureDocuments({ claim }: { claim: ClaimWithRelations }) {
  const { can } = useCurrentUser();
  const deleteDocument = useDeleteProcedureDocument(claim.id);
  const prefetchPreview = usePreviewPrefetch();

  const [previewedFile, setPreviewedFile] = useState<PreviewFile | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const procedureDocuments: ProcedureDocumentItem[] = claim.procedure?.procedureDocuments ?? [];
  const canManage = can([...UPCLAIM_ROLES, "LAWYER"]);

  return (
    <div className="py-4">
      <FilePreviewDialog
        open={previewedFile !== null}
        file={previewedFile}
        onClose={() => setPreviewedFile(null)}
      />
      <AddProcedureDocumentDialog claim={claim} open={addOpen} onOpenChange={setAddOpen} />

      {procedureDocuments.length ? (
        <ul className="col-span-3 mb-5 w-full divide-y divide-gray-100 rounded-md border border-gray-300 bg-white shadow-sm">
          {procedureDocuments.map((procedureDocument) => {
            const TypeIcon = procedureDocumentTypesMap[procedureDocument.type.name].icon;

            return (
              <li
                key={procedureDocument.id}
                {...prefetchPreview(toPreviewFile(procedureDocument.file))}
                className="list-none py-4 pr-5 pl-4 text-sm leading-6"
              >
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    className="flex w-0 flex-1 cursor-pointer items-center text-left"
                    onClick={() => setPreviewedFile(toPreviewFile(procedureDocument.file))}
                  >
                    <TypeIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                    <div className="ml-4 flex min-w-0 flex-1 gap-2">
                      <span className="font-medium">
                        {procedureDocument.type.displayText} ·{" "}
                        <span className="text-xs text-gray-600">
                          {new Date(
                            procedureDocument.date || procedureDocument.createdAt,
                          ).toLocaleDateString()}
                        </span>
                      </span>
                    </div>
                  </button>
                  <div className="ml-4 flex flex-shrink-0 space-x-4">
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <button
                            type="button"
                            onClick={() => setPreviewedFile(toPreviewFile(procedureDocument.file))}
                            className="rounded-md font-medium text-blue-600 hover:text-blue-500"
                            aria-label="Prévisualiser le document"
                          />
                        }
                      >
                        <FileSearch className="h-5 w-5" />
                      </TooltipTrigger>
                      <TooltipContent>{addedAtLabel(procedureDocument.createdAt)}</TooltipContent>
                    </Tooltip>
                    {canManage ? (
                      <>
                        <span className="text-gray-200" aria-hidden="true">
                          |
                        </span>
                        <button
                          type="button"
                          className="rounded-md font-medium text-gray-900 hover:text-gray-800"
                          onClick={() => deleteDocument.mutate(procedureDocument.id)}
                          aria-label="Supprimer le document"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
                {procedureDocument.comment ? (
                  <div className="pt-2 pl-9 text-xs">
                    <span className="prose font-medium text-amber-700">{procedureDocument.comment}</span>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className="col-span-3 mb-5 w-full divide-y divide-gray-100 rounded-md border border-dotted border-gray-300 bg-white shadow-sm">
          <li className="flex items-center justify-between py-4 pr-5 pl-4 text-sm leading-6">
            <div className="flex w-0 flex-1 items-center">
              <Info className="ml-2 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              <div className="ml-4 flex min-w-0 flex-1 gap-2">
                <span className="flex items-center truncate font-medium">
                  Aucun document de procédure pour l'instant
                </span>
                <div className="ml-auto">
                  <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
                    Ajouter
                  </Button>
                </div>
              </div>
            </div>
          </li>
        </ul>
      )}

      {procedureDocuments.length ? (
        <div className="text-left">
          <Button variant="outline" onClick={() => setAddOpen(true)}>
            Ajouter un document
          </Button>
        </div>
      ) : null}
    </div>
  );
}
